import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import Accessibility from '../../components/Accessibility';
import CheckoutFailure from '../CheckoutFailure';
import determineHeaderImage from '../Layout/Header/determineHeaderImage';
import Footer from '../../components/Layout/Footer';
import ReviewTable from './ReviewTable';
import siteInfoById from '../../constants/siteInfoById';

import { connect } from 'react-redux';
import { resetCheckout } from '../../containers/Checkout/checkoutSlice';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import styled from '@mui/system/styled';

import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import { Link } from '@mui/material';

const SuccessPageHeader = styled(Typography)(({ theme }) => ({
	fontSize: '0.8rem',
	fontWeight: '500',
	'@media (min-width:600px)': {
		fontSize: '0.9rem'
	},
	[theme.breakpoints.up('md')]: {
		fontSize: '1.1rem'
	}
}));

const CheckoutSuccess = (props) => {
	const { cartData, checkoutResponse, resetCheckout, siteId, subtotal } = props;

	const handleGoBack = () => {
		window.location.replace(siteInfoById[siteId].homeUrl);
	};

	if (!Object.entries(checkoutResponse).length) {
		return <CheckoutFailure />;
	}

	return (
		<>
			<Box
				className='filterbg'
				sx={{ pb: 25, px: { xs: 3, sm: 10, md: 13, lg: 20, xl: 40 } }}
				mt={2}
			>
				<Stack
					direction='row'
					alignItems='flex-start'
					sx={{ cursor: 'pointer' }}
					onClick={handleGoBack}
					component='header'
					className='filterbg'
				>
					<Box
						component='img'
						src={determineHeaderImage(siteId)}
						alt={`${siteInfoById[siteId].name} Logo`}
						className='filter'
						sx={{
							height: { xs: '39px', md: '60px' }
						}}
					/>
				</Stack>
				<Stack className='filter' spacing={4} sx={{ mt: 2, py: { xs: 1, md: 2 } }}>
					<Typography
						variant='h3'
						sx={{
							fontSize: { xs: '1.2rem', md: '1.95rem' }
						}}
					>
						Order Received — We are working on it
					</Typography>
					<Typography
						variant='h5'
						className='togglebg'
						sx={{
							fontSize: { xs: '1rem', lg: '1.25rem' }
						}}
					>
						<HourglassBottomIcon sx={{ verticalAlign: 'middle', color: 'black' }} /> Thank you — we
						have received your order for gift cards and are getting to work fulfilling it for you.
						You will receive an email from us within 48 hours with delivery information — if you
						don't, please reach out to us at{' '}
						<Link href='mailto:membership@sempris.com' sx={{ color: 'secondary.main' }}>
							membership@sempris.com
						</Link>{' '}
						with any questions!
					</Typography>

					<Button
						onClick={handleGoBack}
						sx={{
							width: 'fit-content',
							mb: {
								xs: '30px !important',
								lg: '0',
								xl: '0'
							},
							fontSize: { xs: '13px', lg: '1rem' },
							textTransform: 'none',
							padding: '8px 30px 8px 25px',
							borderRadius: (theme) => theme.pixels.bRadius.main
						}}
					>
						{`< Back to ${siteInfoById[siteId].name}`}
					</Button>
				</Stack>
				<Stack
					direction='row'
					divider={<Divider orientation='vertical' flexItem />}
					spacing={{ xs: 1, sm: 2, md: 5 }}
				>
					<Stack>
						<SuccessPageHeader fontWeight={500} variant='h5'>
							Order Number
						</SuccessPageHeader>
						<SuccessPageHeader>{checkoutResponse?.orderNumber || ''}</SuccessPageHeader>
					</Stack>
					<Stack>
						<SuccessPageHeader fontWeight={500} variant='h5'>
							Date
						</SuccessPageHeader>
						<SuccessPageHeader>{dayjs().format('M/D/YYYY')}</SuccessPageHeader>
					</Stack>
					<Stack>
						<SuccessPageHeader fontWeight={500} variant='h5'>
							Total
						</SuccessPageHeader>
						<SuccessPageHeader>${subtotal}.00</SuccessPageHeader>
					</Stack>
					<Stack>
						<SuccessPageHeader fontWeight={500} variant='h5'>
							Payment
						</SuccessPageHeader>
						<SuccessPageHeader>Pending Fulfillment</SuccessPageHeader>
					</Stack>
				</Stack>
				<Stack my={5}>
					<ReviewTable cartData={cartData} subtotal={subtotal} />
				</Stack>
				<Typography
					variant='h5'
					className='togglebg'
					sx={{
						fontSize: { xs: '1rem', lg: '1.25rem' }
					}}
				>
					Thank you for being a valued customer. Please reach out to us at{' '}
					<Link href='mailto:membership@sempris.com' sx={{ color: 'secondary.main' }}>
						membership@sempris.com
					</Link>{' '}
					with any questions!
				</Typography>
			</Box>
			<Accessibility />
			<Footer siteId={siteId} hideCart />
		</>
	);
};

CheckoutSuccess.propTypes = {
	cartData: PropTypes.array,
	checkoutResponse: PropTypes.object,
	error: PropTypes.string,
	resetCheckout: PropTypes.func,
	siteId: PropTypes.number,
	subtotal: PropTypes.number
};

const mapStateToProps = (state) => {
	return {
		cartData: state.cartSlice.cartData,
		checkoutResponse: state.checkoutSlice.data,
		siteId: state.authSlice.siteId,
		subtotal: state.cartSlice.subtotal
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		resetCheckout: () => {
			dispatch(resetCheckout());
		}
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(CheckoutSuccess);
